// Tính các biểu chứng từ (UI Biểu 01=tinhBieu02 theo hộ, 02=tinhBieu03 NV, 03=tinhBieu04 HTX)
// từ dữ liệu thật. Mỗi hàm thuần độc lập — không gọi Firestore.

import { soCccdDayDuTuHoSo } from "./cccdBaoMat";

const GHI_CHU_CHUNG_TU_MAC_DINH = "Thu mua trực tiếp từ hộ nông dân";

function diaChiDayDu(cv) {
  if (!cv) return null;
  return [cv.diaChi, cv.thon, cv.xa, cv.tinh].filter(Boolean).join(", ") || null;
}

// ---- Biểu 02: bảng kê chi tiết thu mua, từng dòng (PO, loại bơ) riêng ----
// Không gộp theo (chủ vườn, loại bơ) qua cả kỳ như trước — 1 PO mua nhiều
// loại bơ cùng lúc thì mỗi loại bơ là 1 dòng riêng, đủ vết từng chứng từ đầu
// vào thay vì cộng dồn nhiều lượt khác ngày vào chung 1 dòng.
/**
 * @param {object} p
 * @param {Array} p.danhSachGiaoDich - giaoDichApi.trongKhoang(...) trong kỳ báo cáo.
 * @param {Object<string, object>} p.chuVuonChiTiet - maChuVuon -> chuVuonApi.chiTiet(...).
 */
export function tinhBieu02({ danhSachGiaoDich, chuVuonChiTiet }) {
  const dong = danhSachGiaoDich
    .flatMap((gd) =>
      gd.items.map((it) => ({
        maChuVuon: gd.maChuVuon,
        tenChuVuon: gd.tenChuVuon,
        thoiGian: gd.thoiGian,
        maLoaiBo: it.maLoaiBo,
        tenLoaiBo: it.tenLoaiBo,
        soLuong: it.khoiLuong,
        thanhTien: it.thanhTien,
      }))
    )
    .map((d) => {
      const cv = chuVuonChiTiet[d.maChuVuon];
      return {
        ...d,
        cccd:
          soCccdDayDuTuHoSo(cv) ||
          (/^\d{9,12}$/.test(String(d.maChuVuon ?? "")) ? String(d.maChuVuon) : "") ||
          "—",
        diaChi: diaChiDayDu(cv) ?? "—",
        donGia: d.soLuong > 0 ? d.thanhTien / d.soLuong : 0,
        ghiChu: GHI_CHU_CHUNG_TU_MAC_DINH,
      };
    })
    .sort((a, b) => {
      const tenA = a.tenChuVuon ?? "";
      const tenB = b.tenChuVuon ?? "";
      if (tenA !== tenB) return tenA.localeCompare(tenB, "vi");
      return a.thoiGian < b.thoiGian ? -1 : 1;
    })
    .map((d, i) => ({ stt: i + 1, ...d }));

  const tong = dong.reduce(
    (t, d) => ({ soLuong: t.soLuong + d.soLuong, thanhTien: t.thanhTien + d.thanhTien }),
    { soLuong: 0, thanhTien: 0 }
  );

  return { dong, tong };
}

// ---- Biểu 03: từng PO thu mua (KHÔNG gộp), sắp theo nhân viên ----
/**
 * @param {object} p
 * @param {Array} p.danhSachGiaoDich - giaoDichApi.trongKhoang(...) trong kỳ báo cáo.
 * @param {Array} p.danhSachNhapKho - lichSuKhoApi.trongKhoang(...) đã lọc loai === "nhap".
 * @param {Object<string, Array>} p.itemsTheoChuKy - chuKyId -> mảng PO gốc (PO g?c theo chu k? S?t).
 */
export function tinhBieu03({ danhSachGiaoDich, danhSachNhapKho, itemsTheoChuKy }) {
  // Phân bổ khối lượng nhập kho về đúng từng PO theo tỷ lệ đóng góp của PO đó
  // trong tổng khối lượng cả chu kỳ Sọt — cùng cách làm với Biểu 01, chỉ
  // đổi khoá từ loại bơ sang từng PO.
  const nhapKhoTheoPO = {};
  danhSachNhapKho.forEach((nk) => {
    // Cùng lý do như Biểu 01: itemsTheoChuKy[chuKyId] trả về mọi PO từng
    // chạm chu kỳ này, nhưng 1 PO có thể trải nhiều sọt/chu kỳ khác nhau —
    // chỉ tính phần kg thuộc ĐÚNG chu kỳ nk.chuKyId đang xét (không phải cả
    // PO), nếu không PO trải 2 chu kỳ sẽ bị tính khối lượng của chu kỳ kia
    // vào tỷ lệ phân bổ ở đây.
    const itemsGoc = (nk.chuKyId && itemsTheoChuKy[nk.chuKyId]) || [];
    const tongGoc = itemsGoc.reduce(
      (s, gd) =>
        s +
        gd.items
          .filter((it) => (it.chuKyId ?? gd.chuKyId) === nk.chuKyId)
          .reduce((s2, it) => s2 + it.khoiLuong, 0),
      0
    );
    if (!nk.chuKyId || tongGoc <= 0 || nk.khoiLuongCanLai == null) return;

    itemsGoc.forEach((gd) => {
      const kgPo = gd.items
        .filter((it) => (it.chuKyId ?? gd.chuKyId) === nk.chuKyId)
        .reduce((s, it) => s + it.khoiLuong, 0);
      if (kgPo <= 0) return;
      const tiLe = kgPo / tongGoc;
      nhapKhoTheoPO[gd.id] = (nhapKhoTheoPO[gd.id] ?? 0) + nk.khoiLuongCanLai * tiLe;
    });
  });

  const dong = danhSachGiaoDich
    .map((gd) => {
      const slMua = gd.items.reduce((s, it) => s + it.khoiLuong, 0);
      const tienMua = gd.items.reduce((s, it) => s + it.thanhTien, 0);
      const donGiaMua = slMua > 0 ? tienMua / slMua : 0;
      const slNhapKhoTho = nhapKhoTheoPO[gd.id];
      const chuaNhapKho = slNhapKhoTho == null;
      const slNhapKho = chuaNhapKho ? null : Math.round(slNhapKhoTho * 100) / 100;
      const haoHut = chuaNhapKho ? 0 : Math.round((slNhapKho - slMua) * 100) / 100;
      const tyLeHaoHut = !chuaNhapKho && slMua > 0 ? haoHut / slMua : null;
      return {
        id: gd.id,
        maNhanVien: gd.maNhanVien,
        tenNhanVien: gd.tenNhanVien ?? "—",
        thoiGian: gd.thoiGian,
        tenChuVuon: gd.tenChuVuon ?? "—",
        maSotHienThi: gd.maSotHienThi ?? "—",
        slMua,
        donGiaMua,
        tienMua,
        slNhapKho,
        chuaNhapKho,
        haoHut,
        tyLeHaoHut,
      };
    })
    .sort((a, b) => {
      const tenA = a.tenNhanVien ?? "";
      const tenB = b.tenNhanVien ?? "";
      if (tenA !== tenB) return tenA.localeCompare(tenB, "vi");
      return a.thoiGian < b.thoiGian ? -1 : 1;
    });

  return { dong, tong: tongBieu03TuDong(dong) };
}

// Tách riêng để trang tính lại đúng dòng TỔNG CỘNG khi lọc Biểu 03 theo 1
// nhân viên cụ thể (lọc client-side trên dữ liệu đã tải, không gọi lại
// Firestore).
export function tongBieu03TuDong(dong) {
  const tong = dong.reduce(
    (t, d) => ({
      slMua: t.slMua + d.slMua,
      tienMua: t.tienMua + d.tienMua,
      slNhapKho: t.slNhapKho + (d.slNhapKho ?? 0),
      haoHut: t.haoHut + (d.haoHut ?? 0),
    }),
    { slMua: 0, tienMua: 0, slNhapKho: 0, haoHut: 0 }
  );
  tong.tyLeHaoHut = tong.slMua > 0 ? tong.haoHut / tong.slMua : 0;
  return tong;
}

// ---- Biểu 04: theo điểm thu mua (hợp tác xã), chi tiết từng loại bơ ----
// "Điểm thu mua" hiện tại chỉ có 1 khái niệm gắn với dữ liệu là Hợp tác xã
// (giaoDich.maHopTacXa, chọn ngay trong FormThuMua — field tuỳ chọn, không
// còn là bước riêng). PO nào không gắn HTX (mua trực tiếp, không qua điểm thu mua nào)
// vẫn gộp vào 1 nhóm riêng để tổng biểu khớp tổng Biểu 01, không lặng lẽ bỏ
// sót.
/**
 * @param {object} p
 * @param {Array} p.danhSachGiaoDich - giaoDichApi.trongKhoang(...) trong kỳ báo cáo.
 * @param {Array} [p.dsHopTacXaTatCa] - hopTacXaApi.danhSachTatCa(), TOÀN BỘ hợp tác xã trong hệ
 *   thống (không chỉ hợp tác xã có giao dịch trong kỳ) — để những HTX chưa có giao dịch nào trong
 *   khoảng ngày đang xem vẫn hiện ra (0 kg, 0 đ) thay vì lặng lẽ biến mất khỏi biểu và bộ lọc.
 */
export function tinhBieu04({ danhSachGiaoDich, dsHopTacXaTatCa = [] }) {
  const theoHtx = {};
  function oHtx(maHopTacXa, tenHopTacXa) {
    const khoa = maHopTacXa ?? "__ngoai_htx";
    if (!theoHtx[khoa]) {
      theoHtx[khoa] = {
        maHopTacXa: maHopTacXa ?? null,
        tenHopTacXa: maHopTacXa ? tenHopTacXa ?? "—" : "Ngoài hợp tác xã (mua trực tiếp)",
        theoLoai: {},
      };
    }
    return theoHtx[khoa];
  }

  // Đặt trước MỌI hợp tác xã đang có trong hệ thống — kể cả chưa từng thu mua
  // qua điểm đó trong kỳ này — trước khi cộng dồn giao dịch thật vào.
  dsHopTacXaTatCa.forEach((htx) => oHtx(htx.id, htx.tenHopTacXa));

  // Gộp theo cặp (nhân viên, loại bơ) — không chỉ loại bơ như trước — để biết
  // rõ AI đã thu mua từng dòng, phòng khi 1 điểm HTX có nhiều nhân viên cùng
  // mua cùng loại bơ trong kỳ (mỗi người vẫn tách dòng riêng, không gộp lẫn).
  danhSachGiaoDich.forEach((gd) => {
    const nhom = oHtx(gd.maHopTacXa, gd.tenHopTacXa);
    gd.items.forEach((it) => {
      const khoaDong = `${gd.maNhanVien ?? "__khac"}::${it.maLoaiBo}`;
      if (!nhom.theoLoai[khoaDong]) {
        nhom.theoLoai[khoaDong] = {
          maNhanVien: gd.maNhanVien ?? null,
          tenNhanVien: gd.tenNhanVien ?? "—",
          maLoaiBo: it.maLoaiBo,
          tenLoaiBo: it.tenLoaiBo ?? "—",
          slMua: 0,
          tienMua: 0,
        };
      }
      nhom.theoLoai[khoaDong].slMua += it.khoiLuong;
      nhom.theoLoai[khoaDong].tienMua += it.thanhTien;
    });
  });

  const nhom = Object.values(theoHtx)
    .map((n) => {
      const dong = Object.values(n.theoLoai)
        .map((d) => ({ ...d, donGiaMua: d.slMua > 0 ? d.tienMua / d.slMua : 0 }))
        .sort((a, b) => b.tienMua - a.tienMua);
      const tong = dong.reduce(
        (t, d) => ({ slMua: t.slMua + d.slMua, tienMua: t.tienMua + d.tienMua }),
        { slMua: 0, tienMua: 0 }
      );
      return { maHopTacXa: n.maHopTacXa, tenHopTacXa: n.tenHopTacXa, dong, tong };
    })
    // Trọng tâm biểu này là điểm thu mua HTX — nhóm "ngoài HTX" luôn xếp
    // cuối (chỉ để đối chiếu tổng cho khớp), các HTX còn lại xếp theo thành
    // tiền giảm dần.
    .sort((a, b) => {
      if (!a.maHopTacXa) return 1;
      if (!b.maHopTacXa) return -1;
      return b.tong.tienMua - a.tong.tienMua;
    });

  const tongChung = nhom.reduce(
    (t, n) => ({ slMua: t.slMua + n.tong.slMua, tienMua: t.tienMua + n.tong.tienMua }),
    { slMua: 0, tienMua: 0 }
  );

  return { nhom, tongChung };
}
